import classNames from "classnames";

// Table conventions for property-value columns in list tables
// (docs/frontend_guide.md, "Tabellen") — shared between the contact list
// (contacts/ContactsPage) and the app-creator items table
// (appCreator/ItemsTableView) so the type→width/clamp mapping can't drift.
// Only the genuinely shared per-type mapping lives here; table-specific
// concerns (where whitespace-nowrap sits, align-top, media handling) stay
// at the call sites.

// value-list cells (links, labels, names): wide enough to mostly fit on one
// line, clamped to two visual lines beyond that. file cells are value lists
// too — in table cells their entries render comma-separated inline like the
// other types (td .GenericValueList li is display:inline), the clamp caps
// the wrapped overflow.
export const VALUE_LIST_TYPES = [
  "relation",
  "selection",
  // selections whose meaning belongs to the type (tixxt/core#658, #656) —
  // their cells hold the same value lists
  "salutation",
  "title",
  "membership",
  "tag",
  "address",
  "file",
];

interface ColumnLike {
  type: string;
  multiline?: boolean;
}

// single-line values (short text, email, url): fixed header width as cap,
// one-line truncate in the cell
export function isSingleLineColumn({ type, multiline }: ColumnLike): boolean {
  return (type === "text" && !multiline) || type === "email" || type === "url";
}

// wrapping preview columns (html, multiline text): the only columns WITHOUT
// a header width — they absorb the remaining table width
export function isWrappingColumn({ type, multiline }: ColumnLike): boolean {
  return type === "html" || !!multiline;
}

// th classes: fixed widths so surplus table width flows into the
// unconstrained wrapping columns — auto layout hands extra space to columns
// without a width. No percentage widths: they make Chromium extrapolate the
// table size and blow up the layout.
export function columnHeaderClasses(column: ColumnLike): string {
  return classNames({
    "w-64": isSingleLineColumn(column),
    "w-32": column.type === "image",
    "w-28": column.type === "number" || column.type === "date",
    "w-56": VALUE_LIST_TYPES.includes(column.type),
    "text-right": column.type === "number",
  });
}

// classes for the wrapper div around the cell value; empty string means
// "no wrapper needed" (numbers, dates, media — media must render unwrapped,
// line-clamp would squeeze images)
export function columnCellWrapperClasses(column: ColumnLike): string {
  return classNames({
    // three visual lines; the min keeps auto layout from crushing the column
    "min-w-80 line-clamp-3": isWrappingColumn(column),
    // truncate instead of wrapping, no width claim — leaves room for e.g.
    // image columns
    "max-w-64 truncate": isSingleLineColumn(column),
    "line-clamp-2":
      !isWrappingColumn(column) && VALUE_LIST_TYPES.includes(column.type),
  });
}
